import React, { RefObject, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector, useTranslate } from '../../../hooks/common';
import { ChartImages, ChartUserOptions, CommonData, Curve } from '../../../models';
import Carousel from '../../common/Carousel';
import { Chart, ChartOptions, ChartTypeRegistry, ScaleChartOptions, ScaleOptions, TooltipCallbacks, TooltipItem, registerables } from 'chart.js';
import { CurrentUnit, CurveType, DiagramOptionsProperties, Language, ScalingType, StartingType } from '../../../enums';
import { ABSOLUTE_UNITS, AXIS_IDS, BACKGROUND_COLOR_PLUGIN, CONVERTER_OPERATION_VSD_DIAGRAM_CURVES, CURRENT_OVER_SPEED_DIAGRAM_CURVES, CURRENT_OVER_TIME_DIAGRAM_CURVES, CURRENT_UNITS, DEFAULT_DIAGRAM_OPTIONS, INITIAL_CHART_IMAGES, POWER_OVER_SPEED_DIAGRAM_CURVES, RELATED_UNITS, TIME_OVER_SPEED_DIAGRAM_CURVES, TORQUE_OVER_SPEED_DIAGRAM_CURVES, TRANSLATIONS } from '../../../constants';
import { Api, calculateXAxisMax, deepCopy, getAxisMax, getPowerUnit, getRoundedTooltipLabel, getRoundedTooltipTitle, getTickValue, getTooltipCurveColors, getTorqueUnit, getTwoYAxis } from '../../../services';
import zoomPlugin from 'chartjs-plugin-zoom';
import Section, { SectionColor } from '../../common/Section';
import Slider from 'react-slick';
import Diagram from './Diagram';
import { setChartIndex, setDisabledCurveTypes, setIsAbsoluteScaling, setTags } from '../../../store';
import { DragOptions, LimitOptions, PanOptions } from 'chartjs-plugin-zoom/types/options';
import TabSwitcher, { Tab } from '../../common/TabSwitcher';
import { useHook } from '../../../hooks';
import { showStatusMessage } from '@innomotics/innomotics-build-react-lib';

Chart.register(...registerables, zoomPlugin, BACKGROUND_COLOR_PLUGIN);

export type DocumentImageHandle = {
    getDocumentImages: () => Promise<ChartImages>;
};

interface DiagramCarouselProps {
    tabs: Tab[],
    renderLineOperations: boolean;
    renderConverterOperation: boolean;
}

const DiagramCarousel = forwardRef<DocumentImageHandle, DiagramCarouselProps>(({ tabs, renderLineOperations, renderConverterOperation }, ref) => {
    const translate = useTranslate();
    const dispatch = useAppDispatch();
    const language = useAppSelector(state => state.translation.language);
    const curves = useAppSelector(state => state.diagram.diagramData);
    const isAbsoluteScaling = useAppSelector(state => state.diagram.isAbsoluteScaling);
    const disabledCurveTypes = useAppSelector(state => state.diagram.disabledCurveTypes);
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const calculationResponse = useAppSelector(state => state.motor.calculationResponse);
    const chartIndex = useAppSelector(state => state.diagram.chartIndex);
    const sliderRef = useRef<Slider>(null);
    const torqueOverSpeedChartRef = useRef<Chart<'line'>>(null);
    const currentOverSpeedChartRef = useRef<Chart<'line'>>(null);
    const powerOverSpeedChartRef = useRef<Chart<'line'>>(null);
    const timeOverSpeedChartRef = useRef<Chart<'line'>>(null);
    const currentOverTimeChartRef = useRef<Chart<'line'>>(null);
    const converterOperationVsdChartRef = useRef<Chart<'line'>>(null);
    const [torqueOverSpeedDiagramOptions, setTorqueOverSpeedDiagramOptions] = useState<ChartOptions<'line'>>(() => getDefaultDiagramOptions('torqueOverSpeedDiagramOptions'));
    const [currentOverSpeedDiagramOptions, setCurrentOverSpeedDiagramOptions] = useState<ChartOptions<'line'>>(() => getDefaultDiagramOptions('currentOverSpeedDiagramOptions'));
    const [powerOverSpeedDiagramOptions, setPowerOverSpeedDiagramOptions] = useState<ChartOptions<'line'>>(() => getDefaultDiagramOptions('powerOverSpeedDiagramOptions'));
    const [timeOverSpeedDiagramOptions, setTimeOverSpeedDiagramOptions] = useState<ChartOptions<'line'>>(() => getDefaultDiagramOptions('timeOverSpeedDiagramOptions'));
    const [currentOverTimeDiagramOptions, setCurrentOverTimeDiagramOptions] = useState<ChartOptions<'line'>>(() => getDefaultDiagramOptions('currentOverTimeDiagramOptions'));
    const [converterOperationVsdDiagramOptions, setConverterOperationVsdDiagramOptions] = useState<ChartOptions<'line'>>(() => getDefaultDiagramOptions('converterOperationVsdDiagramOptions'));
    const [labelUpdated, setLabelUpdated] = useState(false);
    const { onDiagramChange } = useHook(x => x.diagram)();

    const previousCalculation = useRef(calculationResponse);
    useEffect(() => {
        // Keep restored curve visibility on mount and when only labels change.
        if (previousCalculation.current !== calculationResponse) {
            dispatch(setDisabledCurveTypes([]));
            previousCalculation.current = calculationResponse;
        }
    }, [calculationResponse]);

    useEffect(() => {
        updateDiagramOptionLabels();
    }, [language, isAbsoluteScaling]);

    useEffect(() => {
        labelUpdated && updateDiagramOptionsAxes();
    }, [labelUpdated, curves]);

    const updateDiagramOptionLabels = () => {
        const { unitSystem } = mesRequest;
        const { currentUnit } = mesRequest.commonData;

        const [torqueOverSpeedDiagramOptions, currentOverSpeedDiagramOptions, powerOverSpeedDiagramOptions, timeOverSpeedDiagramOptions, currentOverTimeDiagramOptions, converterOperationVsdDiagramOptions] = getDiagramOptionsCopies();
        const newXLabel = isAbsoluteScaling ? translate(TRANSLATIONS.units.rpm) : '[n/nsyn]';

        [torqueOverSpeedDiagramOptions, currentOverSpeedDiagramOptions, powerOverSpeedDiagramOptions, timeOverSpeedDiagramOptions, converterOperationVsdDiagramOptions]
            .forEach(x => x.scales[AXIS_IDS.xAxis0].title.text = newXLabel);

        converterOperationVsdDiagramOptions.scales[AXIS_IDS.yAxis0].title.text = isAbsoluteScaling ? getTorqueUnit(unitSystem) : '[M/Mn]';
        torqueOverSpeedDiagramOptions.scales[AXIS_IDS.yAxis0].title.text = isAbsoluteScaling ? getTorqueUnit(unitSystem) : '[M/Mn]';
        currentOverSpeedDiagramOptions.scales[AXIS_IDS.yAxis0].title.text = CURRENT_UNITS.get(currentUnit);
        currentOverTimeDiagramOptions.scales[AXIS_IDS.yAxis0].title.text = CURRENT_UNITS.get(currentUnit);
        powerOverSpeedDiagramOptions.scales[AXIS_IDS.yAxis0].title.text = getPowerUnit(unitSystem);

        [torqueOverSpeedDiagramOptions, currentOverSpeedDiagramOptions, powerOverSpeedDiagramOptions, timeOverSpeedDiagramOptions, currentOverTimeDiagramOptions]
            .forEach(x => {
                (x.plugins.tooltip.callbacks as Partial<TooltipCallbacks<'line'>>) = {
                    labelColor: ctx => getTooltipCurveColors(ctx),
                    title: ctx => getRoundedTooltipTitle(ctx, CURRENT_OVER_TIME_DIAGRAM_CURVES, isAbsoluteScaling, newXLabel),
                    label: ctx => getRoundedTooltipLabel(ctx, isAbsoluteScaling, unitSystem)
                };
            });

        setTorqueOverSpeedDiagramOptions(torqueOverSpeedDiagramOptions);
        setCurrentOverSpeedDiagramOptions(currentOverSpeedDiagramOptions);
        setPowerOverSpeedDiagramOptions(powerOverSpeedDiagramOptions);
        setTimeOverSpeedDiagramOptions(timeOverSpeedDiagramOptions);
        setCurrentOverTimeDiagramOptions(currentOverTimeDiagramOptions);
        setConverterOperationVsdDiagramOptions(converterOperationVsdDiagramOptions);
        setLabelUpdated(true);
    };

    const updateDiagramOptionsAxes = () => {
        const [torqueOverSpeedDiagramOptions, currentOverSpeedDiagramOptions, powerOverSpeedDiagramOptions, timeOverSpeedDiagramOptions, currentOverTimeDiagramOptions, converterOperationVsdDiagramOptions] = getDiagramOptionsCopies();

        ([
            { diagramOption: torqueOverSpeedDiagramOptions, curveTypes: TORQUE_OVER_SPEED_DIAGRAM_CURVES },
            { diagramOption: currentOverSpeedDiagramOptions, curveTypes: CURRENT_OVER_SPEED_DIAGRAM_CURVES },
            { diagramOption: powerOverSpeedDiagramOptions, curveTypes: POWER_OVER_SPEED_DIAGRAM_CURVES },
            { diagramOption: timeOverSpeedDiagramOptions, curveTypes: TIME_OVER_SPEED_DIAGRAM_CURVES },
            { diagramOption: currentOverTimeDiagramOptions, curveTypes: CURRENT_OVER_TIME_DIAGRAM_CURVES },
            { diagramOption: converterOperationVsdDiagramOptions, curveTypes: CONVERTER_OPERATION_VSD_DIAGRAM_CURVES }
        ] as Array<{ diagramOption: ChartOptions<'line'>; curveTypes: CurveType[]; }>).forEach(({ diagramOption, curveTypes }) => {
            const curves = getDiagramCurves(curveTypes);
            const firstYAxisRelatedCurves = curves.filter(x => x.yAxisID === AXIS_IDS.yAxis0);
            if (diagramOption && diagramOption.scales) {
                const xAxisMax = calculateXAxisMax(curves);

                (diagramOption.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>).min = 0;
                (diagramOption.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>).max = xAxisMax;
                (diagramOption?.plugins?.zoom?.limits as LimitOptions)['x'] = { min: 0, max: xAxisMax };
                (diagramOption?.plugins?.zoom?.pan as PanOptions).enabled = false;
                (diagramOption?.plugins?.zoom?.zoom?.drag as DragOptions).enabled = true;

                if (firstYAxisRelatedCurves.length) {
                    (diagramOption.scales[AXIS_IDS.yAxis0] as ScaleOptions<'linear'>).suggestedMax = getAxisMax('y', firstYAxisRelatedCurves);
                }
            }
        });

        setTorqueOverSpeedDiagramOptions(torqueOverSpeedDiagramOptions);
        setCurrentOverSpeedDiagramOptions(currentOverSpeedDiagramOptions);
        setPowerOverSpeedDiagramOptions(powerOverSpeedDiagramOptions);
        setTimeOverSpeedDiagramOptions(timeOverSpeedDiagramOptions);
        setCurrentOverTimeDiagramOptions(currentOverTimeDiagramOptions);
    };

    const updateDiagramOptionsXAxis = (chart: Chart, byPanning = false) => {
        const charts = ([
            { chartRef: torqueOverSpeedChartRef, property: 'torqueOverSpeedDiagramOptions' },
            { chartRef: currentOverSpeedChartRef, property: 'currentOverSpeedDiagramOptions' },
            { chartRef: powerOverSpeedChartRef, property: 'powerOverSpeedDiagramOptions' },
            { chartRef: timeOverSpeedChartRef, property: 'timeOverSpeedDiagramOptions' },
            { chartRef: currentOverTimeChartRef, property: 'currentOverTimeDiagramOptions' },
            { chartRef: converterOperationVsdChartRef, property: 'converterOperationVsdDiagramOptions' }
        ] as { chartRef: RefObject<Chart<'line'>>, property: DiagramOptionsProperties }[])
            .find(({ chartRef }) => chartRef.current && chartRef.current.id === chart.id);
        const diagramOptions = charts && charts.property && getDiagramOptions(charts.property);
        const zoomEnabled = (diagramOptions?.plugins?.zoom?.zoom?.drag as DragOptions).enabled;

        if (diagramOptions && diagramOptions.scales && byPanning !== zoomEnabled) {
            const newDiagramOptions = deepCopyDiagramOptions(diagramOptions);
            const { min: xAxisMin, max: xAxisMax } = chart.scales[AXIS_IDS.xAxis0];
            const firstYAxisRelatedCurves = charts?.chartRef.current?.data.datasets as Curve[];
            const newXLabel = isAbsoluteScaling ? translate(TRANSLATIONS.units.rpm) : '[n/nsyn]';

            newDiagramOptions.scales[AXIS_IDS.xAxis0].min = xAxisMin;
            newDiagramOptions.scales[AXIS_IDS.xAxis0].max = xAxisMax;
            newDiagramOptions.scales[AXIS_IDS.xAxis0].title.text = newXLabel;
            newDiagramOptions.scales[AXIS_IDS.yAxis0].suggestedMax = firstYAxisRelatedCurves && getAxisMax('y', firstYAxisRelatedCurves);

            setDiagramOptions(charts.property, newDiagramOptions);
        }
    };

    function getDefaultDiagramOptions(property: DiagramOptionsProperties): ChartOptions<'line'> {
        const { unitSystem } = mesRequest;
        const diagramOptions = deepCopy(DEFAULT_DIAGRAM_OPTIONS);

        switch (property) {
            case 'torqueOverSpeedDiagramOptions':
                diagramOptions.scales[AXIS_IDS.yAxis0].title.text = getTorqueUnit(unitSystem);
                break;
            case 'currentOverSpeedDiagramOptions':
                diagramOptions.scales[AXIS_IDS.yAxis0].title.text = CURRENT_UNITS.get(CurrentUnit.A);
                break;
            case 'powerOverSpeedDiagramOptions':
                diagramOptions.scales[AXIS_IDS.yAxis0].title.text = getPowerUnit(unitSystem);
                break;
            case 'timeOverSpeedDiagramOptions':
                diagramOptions.scales[AXIS_IDS.yAxis0].title.text = '[s]';
                break;
            case 'currentOverTimeDiagramOptions':
                diagramOptions.plugins.tooltip.mode = 'nearest';
                diagramOptions.hover.mode = 'nearest';
                diagramOptions.scales[AXIS_IDS.xAxis0].title.text = '[s]';
                diagramOptions.scales[AXIS_IDS.yAxis0].title.text = CURRENT_UNITS.get(CurrentUnit.A);
                break;
            case 'converterOperationVsdDiagramOptions': {
                const xLabel = isAbsoluteScaling ? translate(TRANSLATIONS.units.rpm) : '[n/nsyn]';
                const twoYAxis = getTwoYAxis(diagramOptions);

                diagramOptions.scales[AXIS_IDS.xAxis0].title.text = xLabel;
                diagramOptions.scales[AXIS_IDS.yAxis0] = twoYAxis[AXIS_IDS.yAxis0 as keyof ScaleChartOptions<keyof ChartTypeRegistry>];
                diagramOptions.scales[AXIS_IDS.yAxis1] = twoYAxis[AXIS_IDS.yAxis1 as keyof ScaleChartOptions<keyof ChartTypeRegistry>];
                diagramOptions.scales[AXIS_IDS.yAxis0].title.text = getTorqueUnit(unitSystem);
                diagramOptions.scales[AXIS_IDS.yAxis1].title.text = getPowerUnit(unitSystem);
                break;
            }
        }

        diagramOptions.plugins.tooltip.callbacks = {
            labelColor: (ctx: TooltipItem<'line'>) => getTooltipCurveColors(ctx),
            title: (ctx: TooltipItem<'line'>) => getRoundedTooltipTitle(ctx, CURRENT_OVER_TIME_DIAGRAM_CURVES, isAbsoluteScaling, translate(TRANSLATIONS.units.rpm)),
            label: (ctx: TooltipItem<'line'>) => getRoundedTooltipLabel(ctx, isAbsoluteScaling, unitSystem)
        };
        diagramOptions.plugins.zoom.pan.onPanComplete = (context: { chart: Chart }) => updateDiagramOptionsXAxis(context.chart, true);
        diagramOptions.plugins.zoom.zoom.onZoomComplete = (context: { chart: Chart }) => updateDiagramOptionsXAxis(context.chart);
        diagramOptions.scales[AXIS_IDS.xAxis0].ticks.callback = getTickValue;

        return diagramOptions;
    }

    const getDiagramOptions = (property: DiagramOptionsProperties): ChartOptions<'line'> => {
        switch (property) {
            case 'torqueOverSpeedDiagramOptions':
                return torqueOverSpeedDiagramOptions;
            case 'currentOverSpeedDiagramOptions':
                return currentOverSpeedDiagramOptions;
            case 'powerOverSpeedDiagramOptions':
                return powerOverSpeedDiagramOptions;
            case 'timeOverSpeedDiagramOptions':
                return timeOverSpeedDiagramOptions;
            case 'currentOverTimeDiagramOptions':
                return currentOverTimeDiagramOptions;
            case 'converterOperationVsdDiagramOptions':
                return converterOperationVsdDiagramOptions;
        }
    };

    const setDiagramOptions = (property: DiagramOptionsProperties, chartOptions: ChartOptions<'line'>) => {
        switch (property) {
            case 'torqueOverSpeedDiagramOptions':
                return setTorqueOverSpeedDiagramOptions(chartOptions);
            case 'currentOverSpeedDiagramOptions':
                return setCurrentOverSpeedDiagramOptions(chartOptions);
            case 'powerOverSpeedDiagramOptions':
                return setPowerOverSpeedDiagramOptions(chartOptions);
            case 'timeOverSpeedDiagramOptions':
                return setTimeOverSpeedDiagramOptions(chartOptions);
            case 'currentOverTimeDiagramOptions':
                return setCurrentOverTimeDiagramOptions(chartOptions);
            case 'converterOperationVsdDiagramOptions':
                return setConverterOperationVsdDiagramOptions(chartOptions);
        }
    };

    const getDiagramOptionsCopies = () => [
        deepCopyDiagramOptions(torqueOverSpeedDiagramOptions),
        deepCopyDiagramOptions(currentOverSpeedDiagramOptions),
        deepCopyDiagramOptions(powerOverSpeedDiagramOptions),
        deepCopyDiagramOptions(timeOverSpeedDiagramOptions),
        deepCopyDiagramOptions(currentOverTimeDiagramOptions),
        deepCopyDiagramOptions(converterOperationVsdDiagramOptions)
    ];

    const deepCopyDiagramOptions = (chartOptions: ChartOptions<'line'>) => {
        if (chartOptions) {
            const chartOptionsCopy = deepCopy(chartOptions);

            chartOptionsCopy.plugins.tooltip.callbacks = chartOptions?.plugins?.tooltip?.callbacks;
            chartOptionsCopy.plugins.zoom.pan.onPanComplete = chartOptions?.plugins?.zoom?.pan?.onPanComplete;
            chartOptionsCopy.plugins.zoom.zoom.onZoomComplete = chartOptions?.plugins?.zoom?.zoom?.onZoomComplete;
            chartOptionsCopy.scales[AXIS_IDS.xAxis0].ticks.callback = chartOptions && chartOptions.scales && (chartOptions.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>).ticks?.callback;

            return chartOptionsCopy;
        }
    };

    const resetZoom = (property: DiagramOptionsProperties, chartRef: RefObject<Chart<'line'>>, curves: Curve[]) => {
        const { current: chart } = chartRef;

        if (chart) {
            chart.resetZoom();
            const newDiagramOptions = deepCopyDiagramOptions(getDiagramOptions(property));

            (newDiagramOptions.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>).min = 0;
            (newDiagramOptions.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>).max = calculateXAxisMax(curves);

            setDiagramOptions(property, newDiagramOptions);
        }
    };

    const changeZoomBehavior = (property: DiagramOptionsProperties, chartRef: RefObject<Chart<'line'>>) => {
        const { current: chart } = chartRef;

        if (chart) {
            const newDiagramOptions = deepCopyDiagramOptions(getDiagramOptions(property));

            if (newDiagramOptions) {
                const zoomEnabled = newDiagramOptions?.plugins?.zoom?.zoom?.drag?.enabled;
                const { min: xAxisMin, max: xAxisMax } = chart.scales[AXIS_IDS.xAxis0];

                if (newDiagramOptions
                    && newDiagramOptions.scales
                    && newDiagramOptions.plugins
                    && newDiagramOptions.plugins.zoom?.pan
                    && newDiagramOptions.plugins.zoom.zoom
                    && newDiagramOptions.plugins.zoom.zoom.drag) {
                    newDiagramOptions.plugins.zoom.zoom.drag.enabled = !zoomEnabled;
                    newDiagramOptions.plugins.zoom.pan.enabled = zoomEnabled;
                    (newDiagramOptions.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>).min = xAxisMin;
                    (newDiagramOptions.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>).max = xAxisMax;

                    setDiagramOptions(property, newDiagramOptions);
                }
            }
        }
    };

    const handleScalingSelectChange = (value: { [key in ScalingType]?: string }) => {
        const scalingType = Number(Object.keys(value)[0]);
        const isAbsolute = scalingType == ScalingType.Absolute;
        const subset: Partial<CommonData> = isAbsolute ? ABSOLUTE_UNITS : RELATED_UNITS;

        dispatch(setIsAbsoluteScaling(isAbsolute));
        dispatch(setTags({ scaling: scalingType }));

        onDiagramChange(subset);
    };

    const getImageUserOptions = (property: DiagramOptionsProperties): ChartUserOptions => {
        const options = getDiagramOptions(property);
        const xScale = options && options.scales && options.scales[AXIS_IDS.xAxis0] as ScaleOptions<'linear'>;
        const yScale = options && options.scales && options.scales[AXIS_IDS.yAxis0] as ScaleOptions<'linear'>;

        return {
            property: property,
            xAxisLabel: xScale && xScale.title ? xScale.title.text as string : '',
            xAxisScale: xScale ? [xScale.min ?? null, xScale.max ?? null] : [null, null],
            yAxisLabel: yScale && yScale.title ? yScale.title.text as string : '',
            yAxisMax: yScale ? yScale.suggestedMax as number : 0
        };
    };

    const getDocumentImages = async () => {
        const { unitSystem } = mesRequest;
        const { currentUnit, startingType } = mesRequest.commonData;
        const powerUnit = getPowerUnit(unitSystem);
        const converterOperationVsdLabel = isAbsoluteScaling ? translate(TRANSLATIONS.units.rpm) : '[n/nsyn]';
        const isStartingTypeDOL = mesRequest.commonData.startingType === StartingType.DOL;
        let chartImages = INITIAL_CHART_IMAGES;
        const userOptions: ChartUserOptions[] = [];

        if (calculationResponse && curves) {
            renderLineOperations && userOptions.push(getImageUserOptions('torqueOverSpeedDiagramOptions'));
            renderLineOperations && userOptions.push(getImageUserOptions('currentOverSpeedDiagramOptions'));
            renderLineOperations && isStartingTypeDOL && userOptions.push(getImageUserOptions('powerOverSpeedDiagramOptions'));
            renderLineOperations && userOptions.push(getImageUserOptions('timeOverSpeedDiagramOptions'));
            renderLineOperations && userOptions.push(getImageUserOptions('currentOverTimeDiagramOptions'));
            renderConverterOperation && userOptions.push(getImageUserOptions('converterOperationVsdDiagramOptions'));

            const resposne = await Api.getChartImages({
                torqueUnit: getTorqueUnit(unitSystem),
                currentUnit: CURRENT_UNITS.get(currentUnit) ?? '',
                powerUnit,
                converterOperationVsdLabel,
                userOptions: userOptions,
                curveData: calculationResponse.curveData,
                startingType,
                disabledCurveTypes
            });

            if (resposne) {
                chartImages = resposne;
            } else {
                showStatusMessage({
                    message: translate(TRANSLATIONS.snackbar.error.chartImageGeneration),
                    showProgress: true,
                    type: 'error'
                });
            }
        }

        return chartImages;
    };

    useImperativeHandle(ref, () => ({ getDocumentImages }));

    const getDiagramCurves = (curveTypes: CurveType[]) => curves ? curves.filter(x => curveTypes.some(y => y === x.curveType)) : [];

    const handleTabSelect = (index: number) => sliderRef && sliderRef.current?.slickGoTo(index);

    return (
        <Section color={SectionColor.DeepBlue}>
            <div className='diagram'>
                <Carousel key={0} sliderRef={sliderRef} slidesToShow={1} initialSlide={chartIndex} afterChange={i => dispatch(setChartIndex(i))}>
                    {renderLineOperations &&
                        <Diagram
                            optionProperties={'torqueOverSpeedDiagramOptions'}
                            onZoomChange={changeZoomBehavior}
                            onScalingChange={handleScalingSelectChange}
                            onZoomReset={resetZoom}
                            curves={getDiagramCurves(TORQUE_OVER_SPEED_DIAGRAM_CURVES)}
                            diagramOptions={torqueOverSpeedDiagramOptions}
                            lineRef={torqueOverSpeedChartRef}
                        />}
                    {renderLineOperations &&
                        <Diagram
                            optionProperties={'currentOverSpeedDiagramOptions'}
                            onZoomChange={changeZoomBehavior}
                            onScalingChange={handleScalingSelectChange}
                            onZoomReset={resetZoom}
                            curves={getDiagramCurves(CURRENT_OVER_SPEED_DIAGRAM_CURVES)}
                            diagramOptions={currentOverSpeedDiagramOptions}
                            lineRef={currentOverSpeedChartRef}
                        />}
                    {renderLineOperations &&
                        <Diagram
                            optionProperties={'powerOverSpeedDiagramOptions'}
                            onZoomChange={changeZoomBehavior}
                            onScalingChange={handleScalingSelectChange}
                            onZoomReset={resetZoom}
                            curves={getDiagramCurves(POWER_OVER_SPEED_DIAGRAM_CURVES)}
                            diagramOptions={powerOverSpeedDiagramOptions}
                            lineRef={powerOverSpeedChartRef}
                        />}
                    {renderLineOperations &&
                        <Diagram
                            optionProperties={'timeOverSpeedDiagramOptions'}
                            onZoomChange={changeZoomBehavior}
                            onScalingChange={handleScalingSelectChange}
                            onZoomReset={resetZoom}
                            curves={getDiagramCurves(TIME_OVER_SPEED_DIAGRAM_CURVES)}
                            diagramOptions={timeOverSpeedDiagramOptions}
                            lineRef={timeOverSpeedChartRef}
                        />}
                    {renderLineOperations &&
                        <Diagram
                            optionProperties={'currentOverTimeDiagramOptions'}
                            onZoomChange={changeZoomBehavior}
                            onScalingChange={handleScalingSelectChange}
                            onZoomReset={resetZoom}
                            curves={getDiagramCurves(CURRENT_OVER_TIME_DIAGRAM_CURVES)}
                            diagramOptions={currentOverTimeDiagramOptions}
                            lineRef={currentOverTimeChartRef}
                        />}
                    {renderConverterOperation &&
                        <Diagram
                            optionProperties={'converterOperationVsdDiagramOptions'}
                            onZoomChange={changeZoomBehavior}
                            onScalingChange={handleScalingSelectChange}
                            onZoomReset={resetZoom}
                            curves={getDiagramCurves(CONVERTER_OPERATION_VSD_DIAGRAM_CURVES)}
                            diagramOptions={converterOperationVsdDiagramOptions}
                            lineRef={converterOperationVsdChartRef}
                        />}
                </Carousel>
                <TabSwitcher dark tabs={tabs} selectedTab={chartIndex} onSelect={handleTabSelect} />
            </div>
        </Section>
    );
});

DiagramCarousel.displayName = 'DiagramCarousel';
export default DiagramCarousel;
